/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export type ApplePayBridgeContext = 'pdp' | 'cart';

interface CartHandoffProductItem {
    productId?: string;
    quantity?: number;
    optionItems?: unknown[];
    bundledProductItems?: unknown[];
    bonusProductLineItem?: boolean;
    shipmentId?: string;
}

interface CartHandoffBasket {
    basketId?: string;
    lastModified?: string;
    currency?: string;
    productSubTotal?: number;
    productTotal?: number;
    productItems?: CartHandoffProductItem[];
    couponItems?: Array<{ code?: string; valid?: boolean }>;
    shipments?: Array<{ shipmentId?: string; c_fromStoreId?: unknown }>;
    giftCertificateItems?: unknown[];
}

export interface CartHandoffSnapshot {
    sourceBasketId: string;
    sourceLastModified: string;
    currencyCode: 'USD';
    productItems: Array<{ productId: string; quantity: number }>;
    couponCodes: string[];
    expectedProductSubTotal: number;
    expectedProductTotal: number;
    sourceFingerprint: string;
}

const unsupportedCart = () => new Error('Apple Pay is unavailable for this cart');

/**
 * Reduce an authoritative SCAPI basket to the non-sensitive values SFCC needs to rebuild it.
 * Prices are comparison-only totals; the native basket is always repriced by Commerce.
 */
export function createCartHandoffSnapshot(basket: CartHandoffBasket): CartHandoffSnapshot {
    const productItems = basket.productItems;
    const couponItems = basket.couponItems ?? [];
    if (
        typeof basket.basketId !== 'string' ||
        !/^[A-Za-z0-9_-]{1,128}$/.test(basket.basketId) ||
        typeof basket.lastModified !== 'string' ||
        basket.lastModified.length < 1 ||
        basket.lastModified.length > 100 ||
        basket.currency !== 'USD' ||
        !Array.isArray(productItems) ||
        productItems.length < 1 ||
        productItems.length > 50 ||
        !Number.isFinite(basket.productSubTotal) ||
        !Number.isFinite(basket.productTotal) ||
        (basket.productSubTotal as number) < 0 ||
        (basket.productTotal as number) < 0 ||
        (basket.shipments?.length ?? 0) > 1 ||
        basket.shipments?.some((shipment) => Boolean(shipment.c_fromStoreId)) ||
        Boolean(basket.giftCertificateItems?.length) ||
        couponItems.length > 10
    ) {
        throw unsupportedCart();
    }

    const normalizedItems = productItems.map((item) => {
        if (
            typeof item.productId !== 'string' ||
            !/^[A-Za-z0-9_-]{1,100}$/.test(item.productId) ||
            !Number.isInteger(item.quantity) ||
            (item.quantity as number) < 1 ||
            (item.quantity as number) > 99 ||
            Boolean(item.optionItems?.length) ||
            Boolean(item.bundledProductItems?.length) ||
            item.bonusProductLineItem === true
        ) {
            throw unsupportedCart();
        }
        return { productId: item.productId, quantity: item.quantity as number };
    });
    normalizedItems.sort((left, right) =>
        left.productId === right.productId
            ? left.quantity - right.quantity
            : left.productId.localeCompare(right.productId)
    );

    const couponCodes = couponItems.map((coupon) => {
        if (
            coupon.valid === false ||
            typeof coupon.code !== 'string' ||
            coupon.code.length < 1 ||
            coupon.code.length > 100
        ) {
            throw unsupportedCart();
        }
        return coupon.code;
    });
    couponCodes.sort();

    const comparison = {
        sourceLastModified: basket.lastModified,
        currencyCode: 'USD' as const,
        productItems: normalizedItems,
        couponCodes,
        expectedProductSubTotal: basket.productSubTotal as number,
        expectedProductTotal: basket.productTotal as number,
    };
    return {
        sourceBasketId: basket.basketId,
        ...comparison,
        sourceFingerprint: JSON.stringify(comparison),
    };
}

const CONTROLLER_NAME = 'ApplePayBridge-Button';

export function buildApplePayBridgeUrl(
    siteId: string,
    localeId: string,
    context: ApplePayBridgeContext,
    sku?: string,
    handoffToken?: string
) {
    const storefrontLocale = localeId.replaceAll('-', '_');
    const base = `/on/demandware.store/Sites-${encodeURIComponent(siteId)}-Site/${encodeURIComponent(storefrontLocale)}/${CONTROLLER_NAME}`;
    const query = new URLSearchParams({ context });
    if (context === 'pdp' && sku) query.set('sku', sku);
    if (context === 'cart' && handoffToken) query.set('handoff', handoffToken);
    return `${base}?${query.toString()}`;
}

export function completedOrderNumber(value: string, storefrontOrigin: string): string | null {
    try {
        const url = new URL(value, storefrontOrigin);
        if (url.origin !== storefrontOrigin) return null;
        const routeName = url.pathname.split('/').pop();
        if (routeName !== 'COPlaceOrder-Submit' && routeName !== 'COPlaceOrder-SubmitOrderConformation') {
            return null;
        }
        const orderNo = url.searchParams.get('order_id') || url.searchParams.get('ID');
        return orderNo && /^[A-Za-z0-9_-]{1,64}$/.test(orderNo) ? orderNo : null;
    } catch {
        return null;
    }
}
